import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateDb, formatDateHourDb } from "../utils/tools";
import {
  customDateFormatType,
  formatDateForInput,
  formatNumber,
  formatPriceUS,
  formatPriceUSData,
} from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.lockingManagement;
const queryKey = "individualServiceFee";

async function fetchIndividualServiceFee(
  currentPage,
  search,
  lockingId,
  period,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (lockingId) options.lockingId = lockingId;
    if (period) options.period = period;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI(
      "get",
      `/totalCommission/individualServiceFee?${queryParams}`,
    );
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        code: item?.employee?.code || item?.employeeObject?.code || "-",
        fullName:
          item?.employee?.fullName || item?.employeeObject?.fullName || "-",
        saleBlock:
          item?.employee?.saleBlock?.name ||
          item?.employeeObject?.saleBlock?.name ||
          "",
        saleArea:
          item?.employee?.saleArea?.name ||
          item?.employeeObject?.saleArea?.name ||
          "",
        remainingRevenueAfterTax: `${formatPriceUS(
          item?.remainingRevenueAfterTax || 0,
        )} VNĐ`,
        commissionPrice: `${formatPriceUS((item?.remainingRevenueAfterTax || 0) - (item?.remainingCommission || 0))} VNĐ`,
        // PDV còn lại: dùng formatPriceUS (có làm tròn về đồng) cho đồng bộ với 2
        // cột trên, tránh hiện đuôi lẻ kiểu "0.136 VNĐ".
        // Chặn âm do lệch làm tròn 1 đồng (chỉ hiển thị, không đổi dữ liệu).
        remainingCommission: `${formatPriceUS(
          Math.max(0, item?.remainingCommission || 0),
        )} VNĐ`,
      };
    });

    return {
      data: newData,
      dataOrigin: data?.items,
      totalPages,
      entries,
      startEntry,
      endEntry,
    };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useIndividualServiceFee(search, lockingId, period) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, search, lockingId, period],
    queryFn: () =>
      fetchIndividualServiceFee(currentPage, search, lockingId, period),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    lockingManagementData: data?.data,
    dataOrigin: data?.dataOrigin,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
